<script setup lang="ts">
import {computed, ref, watch} from 'vue'
import {useI18n} from 'vue-i18n'
import {useNow} from '@vueuse/core'
import ButtonComponent from '@toolify/client/src/modules/ui/components/ButtonComponent.vue'
import IconComponent from '@toolify/client/src/modules/ui/components/IconComponent.vue'
import DropdownComponent from '@toolify/client/src/modules/modals/components/DropdownComponent.vue'
import ChooseUsersComponent from '@toolify/client/src/modules/entities/components/ChooseUsersComponent.vue'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {SizeVariantType} from '@toolify/client/src/modules/ui/enum/SizeVariantType'
import {StyleVariantType} from '@toolify/client/src/modules/ui/enum/StyleVariantType'
import {PermissionEntityType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/PermissionEntityType'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'
import {IResponseEntity} from '@toolify/server/src/services/EntityService/types/IResponseEntity'
import {
  IUserResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IUserResponseInstance'
import {
  IGroupResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IGroupResponseInstance'
import {useViewAsStore} from '@toolify/client/src/stores/ViewAsStore/useViewAsStore'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {
  useWorkspaceMemberApiStore,
} from '@toolify/client/src/stores/api/WorkspaceMemberApiStore/useWorkspaceMemberApiStore'
import {useGroupApiStore} from '@toolify/client/src/stores/api/GroupApiStore/useGroupApiStore'
import {getEntityNameByEntity} from '@toolify/client/src/modules/entities/functions/getEntityNameByEntity'
import {getSeatGroupTitle} from '@toolify/client/src/pages/WorkspaceSettings/constants/groupSeatGroupMeta'
import {useMessages} from '@toolify/client/src/modules/notifications/composables/useMessages'

const {t} = useI18n()
const messages = useMessages()
const viewAsStore = useViewAsStore()
const authStore = useAuthStore()
const workspaceMemberApiStore = useWorkspaceMemberApiStore()
const groupApiStore = useGroupApiStore()

const now = useNow({interval: 30 * 1000})

const isRole = computed(() => viewAsStore.targetEntityType === PermissionEntityType.Group)

const remainingMinutes = computed(() => {
  const expiresAt = viewAsStore.expiresAt
  if(!expiresAt) {
    return null
  }
  return Math.max(0, Math.ceil((expiresAt.getTime() - now.value.getTime()) / 60000))
})

watch(remainingMinutes, minutes => {
  if(minutes === 0) {
    void viewAsStore.stop()
  }
}, {immediate: true})

function stopViewAs() {
  void viewAsStore.stop()
}

const isSwitcherOpen = ref(false)
const isSwitching = ref(false)
const isFetchingTargets = ref(false)
const memberEntities = ref<IResponseEntity[] | null>(null)
const roles = ref<IGroupResponseInstance[] | null>(null)

const members = computed<IUserResponseInstance[] | null>(() => {
  return memberEntities.value?.map(entity => entity.payload as IUserResponseInstance) ?? null
})

const selectedMemberIds = computed(() => isRole.value ? [] : [viewAsStore.state.targetEntityId])

const selectedRoleIds = computed(() => isRole.value ? [viewAsStore.state.targetEntityId] : [])

const hiddenEntityIds = computed(() => {
  const currentEntityId = authStore.currentEntity?.entityId
  return currentEntityId ? [currentEntityId] : []
})

async function loadTargets() {
  const workspaceId = viewAsStore.state.workspaceId
  if(memberEntities.value || isFetchingTargets.value || !workspaceId) {
    return
  }
  isFetchingTargets.value = true
  try {
    const [{members: workspaceMembers}, {groups}] = await Promise.all([
      workspaceMemberApiStore.getWorkspaceMembers(workspaceId, {withoutViewAs: true}),
      groupApiStore.getGroups(workspaceId, {withoutViewAs: true}),
    ])
    memberEntities.value = workspaceMembers
      .map(member => member.entity)
      .filter(entity => entity.entityType === EntityType.User && entity.payload)
    roles.value = groups.map(group => ({
      ...group,
      title: getSeatGroupTitle(t, group, t('general.noName')),
    }))
  } catch {
    memberEntities.value = []
    roles.value = []
  } finally {
    isFetchingTargets.value = false
  }
}

function toggleSwitcher() {
  isSwitcherOpen.value = !isSwitcherOpen.value
  if(isSwitcherOpen.value) {
    void loadTargets()
  }
}

async function switchTo(targetEntityType: PermissionEntityType, targetEntityId: string, targetLabel: string) {
  isSwitcherOpen.value = false
  const isCurrentTarget = targetEntityType === viewAsStore.state.targetEntityType
    && targetEntityId === viewAsStore.state.targetEntityId
  if(isCurrentTarget) {
    return
  }
  isSwitching.value = true
  try {
    await viewAsStore.start({
      workspaceId: viewAsStore.state.workspaceId,
      targetEntityType,
      targetEntityId,
      targetLabel,
    })
  } catch {
    messages.error(t('viewAs.switchFailed'))
  } finally {
    isSwitching.value = false
  }
}

function onChooseMember(userId: string) {
  const entity = memberEntities.value?.find(memberEntity => memberEntity.entityId === userId)
  if(!entity) {
    return
  }
  void switchTo(PermissionEntityType.User, userId, getEntityNameByEntity(entity))
}

function onChooseRole(groupId: string) {
  const role = roles.value?.find(group => group.id === groupId)
  if(!role) {
    return
  }
  void switchTo(PermissionEntityType.Group, groupId, role.title)
}
</script>

<template>
  <div class="view-as-banner">
    <div class="banner-text">
      <IconComponent :name="IconNameType.Eye"/>
      <p class="message">
        {{isRole
          ? t('viewAs.banner.viewingAsRole', {name: viewAsStore.targetLabel})
          : t('viewAs.banner.viewingAsUser', {name: viewAsStore.targetLabel})}}
      </p>
      <span
        v-if="remainingMinutes !== null"
        class="remaining"
      >
        {{t('viewAs.banner.remaining', {minutes: remainingMinutes})}}
      </span>
    </div>
    <div class="banner-actions">
      <DropdownComponent
        :isOpen="isSwitcherOpen"
        placement="bottom"
        @hide="isSwitcherOpen = false"
      >
        <template #default>
          <ButtonComponent
            :size="SizeVariantType.Small"
            :style="StyleVariantType.ClearFilled"
            :isLoading="isSwitching"
            hasArrow
            arrowPlacement="down"
            @click="toggleSwitcher"
          >
            {{t('viewAs.banner.switchTarget')}}
          </ButtonComponent>
        </template>
        <template #dropdown>
          <ChooseUsersComponent
            :resourceType="ResourceType.Workspace"
            :resourceId="viewAsStore.state.workspaceId"
            :availableValues="members"
            :availableUserGroups="roles"
            :selectedEntityIds="selectedMemberIds"
            :selectedUserGroupIds="selectedRoleIds"
            :filteredEntityIds="hiddenEntityIds"
            :isFetching="isFetchingTargets"
            :hasMultipleValues="false"
            @choose="onChooseMember"
            @chooseGroup="onChooseRole"
            @exit="isSwitcherOpen = false"
          />
        </template>
      </DropdownComponent>
      <ButtonComponent
        :size="SizeVariantType.Small"
        :style="StyleVariantType.ClearFilled"
        @click="stopViewAs"
      >
        {{t('viewAs.banner.exit')}}
      </ButtonComponent>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@toolify/client/src/styles/variables' as variables;

.view-as-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: map-get(variables.$spacing, space-3);
  box-sizing: border-box;
  padding: map-get(variables.$spacing, space-2) map-get(variables.$spacing, space-4);
  background: map-get(variables.$colors, tertiary-container);
  color: map-get(variables.$colors, on-tertiary-container);

  @media (max-width: variables.$breakpointLargeMax) {
    padding-top: calc(#{variables.$safeAreaTop} + #{map-get(variables.$spacing, space-2)});
    padding-left: calc(#{variables.$safeAreaLeft} + #{map-get(variables.$spacing, space-4)});
    padding-right: calc(#{variables.$safeAreaRight} + #{map-get(variables.$spacing, space-4)});
  }
}

.banner-text {
  display: flex;
  align-items: center;
  gap: map-get(variables.$spacing, space-2);
  min-width: 0;
}

.message {
  margin: 0;
  font-size: map-get(variables.$textSizes, text-sm);
  font-weight: 500;
  line-height: 1.3;
}

.remaining {
  font-size: map-get(variables.$textSizes, text-sm);
  opacity: 0.8;
  white-space: nowrap;
}

.banner-actions {
  display: flex;
  align-items: center;
  gap: map-get(variables.$spacing, space-2);

  :deep(.dropdown-wrapper) {
    width: auto;
    height: auto;
  }
}
</style>
